import { useState } from "react";
import Modal from "@/components/base/Modal";
import { ROLE_META, formatDate, initialsOf, type Profile } from "../userMeta";
import { useUserActivity } from "../activity";
import ActivityOverview from "./ActivityOverview";
import ActivityLearning from "./ActivityLearning";
import ActivityExams from "./ActivityExams";
import ActivityContent from "./ActivityContent";
import ActivityCertificates from "./ActivityCertificates";
import StudentPackageControl from "./StudentPackageControl";

interface UserDetailModalProps {
  open: boolean;
  onClose: () => void;
  profile: Profile | null;
  onEdit: () => void;
}

type TabKey = "overview" | "learning" | "exams" | "content" | "certificates";

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "overview", label: "Özet", icon: "ri-dashboard-line" },
  { key: "learning", label: "Öğrenme", icon: "ri-book-open-line" },
  { key: "exams", label: "Sınavlar", icon: "ri-file-list-3-line" },
  { key: "content", label: "İçerik", icon: "ri-sticky-note-line" },
  { key: "certificates", label: "Sertifikalar", icon: "ri-award-line" },
];

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-background-100/60 px-4 py-3">
      <p className="text-xs text-foreground-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-foreground-900">{value || "—"}</p>
    </div>
  );
}

export default function UserDetailModal({
  open,
  onClose,
  profile,
  onEdit,
}: UserDetailModalProps) {
  const [tab, setTab] = useState<TabKey>("overview");
  const { data: activity, loading, error } = useUserActivity(profile?.user_id ?? null);

  if (!profile) return null;

  const meta = ROLE_META[profile.role ?? ""] ?? {
    label: profile.role ?? "Bilinmiyor",
    badge: "bg-background-100 text-foreground-600",
    icon: "ri-user-line",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Kullanıcı Detayı"
      titleId="user-detail-title"
      id="user-detail-modal"
      maxWidthClass="max-w-3xl"
    >
      <div className="space-y-5">
        {/* Başlık + temel bilgi */}
        <div className="flex items-start gap-4">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 text-xl font-semibold shrink-0">
            {initialsOf(profile.full_name ?? "?")}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-lg font-bold text-foreground-950 truncate">
              {profile.full_name ?? "—"}
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${meta.badge}`}
              >
                <i className={`${meta.icon} text-sm`} aria-hidden="true"></i>
                {meta.label}
              </span>
              {profile.grade && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                  {profile.grade}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="h-9 px-3 flex items-center justify-center gap-1.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-pencil-line text-base" aria-hidden="true"></i>
            Düzenle
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="E-posta" value={profile.email ?? ""} />
          <Field label="Sınıf / Seviye" value={profile.grade ?? ""} />
          <Field label="İl" value={profile.city ?? ""} />
          <Field label="Doğum Tarihi" value={profile.birth_date ? formatDate(profile.birth_date) : ""} />
          <Field label="Hedef Sınav" value={profile.target_exam ?? ""} />
          <Field label="Kayıt Tarihi" value={profile.created_at ? formatDate(profile.created_at) : ""} />
        </div>

        {profile.role === "ogrenci" && profile.user_id && <StudentPackageControl studentId={profile.user_id} />}

        {/* Sekmeler */}
        <div className="flex p-1 rounded-full bg-background-100 overflow-x-auto" role="tablist" aria-label="Detay sekmeleri">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === t.key
                  ? "bg-background-50 text-primary-700"
                  : "text-foreground-500 hover:text-foreground-800"
              }`}
            >
              <i className={`${t.icon} text-sm`} aria-hidden="true"></i>
              {t.label}
            </button>
          ))}
        </div>

        {/* Aktivite içeriği */}
        <div className="min-h-[120px]">
          {loading ? (
            <div className="py-10 flex items-center justify-center text-foreground-500">
              <i className="ri-loader-4-line text-xl animate-spin mr-2" aria-hidden="true"></i>
              <span className="text-sm">Aktivite verileri yükleniyor...</span>
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <p className="text-sm text-foreground-600">Aktivite verileri yüklenemedi.</p>
              <p className="mt-1 text-xs text-foreground-500">{error}</p>
            </div>
          ) : !profile.user_id ? (
            <p className="text-sm text-foreground-500 text-center py-8">
              Bu kullanıcı hesabını henüz tamamlamamış, aktivite verisi yok.
            </p>
          ) : tab === "overview" ? (
            <ActivityOverview activity={activity} />
          ) : tab === "learning" ? (
            <ActivityLearning activity={activity} />
          ) : tab === "exams" ? (
            <ActivityExams activity={activity} />
          ) : tab === "content" ? (
            <ActivityContent activity={activity} />
          ) : (
            <ActivityCertificates activity={activity} />
          )}
        </div>
      </div>
    </Modal>
  );
}
